import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CORE_STAFF_ROLES } from '../config/roleConfig';
import { secureApi } from '../services/secureApi';
import DesktopLayout from '../layouts/DesktopLayout';
import GuestRequestsHistory from '../components/GuestRequestsHistory';
import { formatDate } from '../utils/dateFormatter';
import '../styles/GuestDetailsPage.css';

const GuestDetailsPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [guest, setGuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeNavItem, setActiveNavItem] = useState('guests');

  useEffect(() => {
    fetchGuestDetails();
  }, [id]);

  const fetchGuestDetails = async () => {
    try {
      setLoading(true);

      const response = await secureApi.get(`/api/guests/${id}`);

      if (response.data.success) {
        setGuest(response.data.guest);
      }
    } catch (err) {
      console.error('Error fetching guest details:', err);
      setError('حدث خطأ في جلب بيانات الضيف');
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount) => {
    if (!amount) return '0';
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const getValueTierBadge = (tier) => {
    const badges = {
      platinum: { label: 'بلاتيني', className: 'badge-platinum' },
      vip: { label: 'VIP', className: 'badge-vip' },
      returning: { label: 'متكرر', className: 'badge-returning' },
      new: { label: 'جديد', className: 'badge-new' }
    };
    return badges[tier] || badges.new;
  };

  const getSourceLabel = (source) => {
    const labels = {
      direct: 'Host Me',
      airbnb: 'Airbnb',
      booking: 'Booking.com',
      expedia: 'Expedia',
      ical: 'iCal',
      gathern: 'Gathern',
      almosafer: 'المسافر',
      manual: 'يدوي',
      other: 'آخر'
    };
    return labels[source] || source;
  };

  const getStatusLabel = (status) => {
    const labels = {
      upcoming: 'حجز مؤكد',
      active: 'نشط',
      completed: 'مكتمل',
      cancelled: 'ملغي'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem}>
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  if (error || !guest) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem}>
        <div className="error-container">
          <i className="bi bi-exclamation-circle"></i>
          <p>{error || 'الضيف غير موجود'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/guests')}>
            العودة للضيوف
          </button>
        </div>
      </DesktopLayout>
    );
  }

  const tierBadge = getValueTierBadge(guest.valueTier);

  // Phase 0 — tasks/guest-reviews-loop-v0.md §0.1 / D28.
  // The backend only sends `conditionRating` to core staff (R11), so the column
  // disappears entirely for owners rather than rendering empty. `null` means
  // "the clean happened but nobody rated it"; `undefined` means "not shown to you".
  const showConditionRating = guest.bookings?.some(b => b.conditionRating !== undefined);

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="تفاصيل الضيف"
    >
      <div className="guest-details-page">
        {/* Guest Header */}
        <div className="guest-header">
          <button className="btn-back" onClick={() => navigate('/guests')}>
            <i className="bi bi-arrow-right"></i> العودة
          </button>

          <div className="guest-title">
            <div className="guest-avatar">
              {guest.name.charAt(0).toUpperCase()}
            </div>
            <div className="guest-info">
              <h1>{guest.name}</h1>
              <span className={`value-tier-badge ${tierBadge.className}`}>
                {tierBadge.label}
              </span>
            </div>
          </div>
        </div>

        {/* Guest Info Cards */}
        <div className="guest-info-grid">
          {/* Contact Information */}
          <div className="info-card">
            <h3>معلومات الاتصال</h3>
            <div className="info-row">
              <i className="bi bi-envelope"></i>
              <span className="label">البريد الإلكتروني:</span>
              <span className="value" style={{ direction: 'ltr', textAlign: 'left' }}>{guest.email || '-'}</span>
            </div>
            <div className="info-row">
              <i className="bi bi-telephone"></i>
              <span className="label">رقم الهاتف:</span>
              <span className="value" style={{ direction: 'ltr', textAlign: 'left' }}>{guest.phone || '-'}</span>
            </div>
            <div className="info-row">
              <i className="bi bi-globe"></i>
              <span className="label">اللغة المفضلة:</span>
              <span className="value">{guest.preferredLanguage === 'ar' ? 'العربية' : 'English'}</span>
            </div>
          </div>

          {/* Booking Statistics */}
          <div className="info-card">
            <h3>إحصائيات الحجوزات</h3>
            <div className="info-row">
              <i className="bi bi-calendar3"></i>
              <span className="label">إجمالي الحجوزات:</span>
              <span className="value highlight">{guest.totalBookings}</span>
            </div>
            <div className="info-row">
              <i className="bi bi-cash-stack"></i>
              <span className="label">إجمالي الإنفاق:</span>
              <span className="value highlight">{formatCurrency(guest.totalRevenue)}</span>
            </div>
            <div className="info-row">
              <i className="bi bi-calendar-check"></i>
              <span className="label">آخر حجز:</span>
              <span className="value">{formatDate(guest.lastBookingDate)}</span>
            </div>
          </div>

          {/* Source & Dates */}
          <div className="info-card">
            <h3>معلومات إضافية</h3>
            <div className="info-row">
              <i className="bi bi-building"></i>
              <span className="label">مصدر التسجيل:</span>
              <span className="value">{getSourceLabel(guest.acquisitionSource)}</span>
            </div>
            <div className="info-row">
              <i className="bi bi-calendar-plus"></i>
              <span className="label">أول حجز:</span>
              <span className="value">{formatDate(guest.firstStayDate)}</span>
            </div>
            <div className="info-row">
              <i className="bi bi-clock-history"></i>
              <span className="label">تاريخ الإنشاء:</span>
              <span className="value">{formatDate(guest.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        {guest.notes && (
          <div className="info-card">
            <h3>ملاحظات</h3>
            <p>{guest.notes}</p>
          </div>
        )}

        {/* Guest Requests Hub V1 — Phase 5: cross-booking request history +
            guest-centric creation flow. Mobile-first placement: primary action
            surface (طلب جديد) sits above the passive booking history table. */}
        {/* Core staff only: guest requests are read by core staff alone (be/routes/guestRequestRoutes.js), and
            other staff roles can open this page since 8c batch 3 (decision D). */}
        {CORE_STAFF_ROLES.includes(user?.role) && <GuestRequestsHistory guest={guest} />}

        {/* Booking History */}
        <div className="booking-history">
          <h3>سجل الحجوزات ({guest.bookings?.length || 0})</h3>

          {guest.bookings?.length > 0 ? (
            <div className="bookings-table-container">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th>رقم الحجز</th>
                    <th>العقار</th>
                    <th>تاريخ الوصول</th>
                    <th>تاريخ المغادرة</th>
                    <th>المبلغ</th>
                    <th>الحالة</th>
                    {showConditionRating && (
                      <th title="تقييم فريق النظافة لحالة الوحدة بعد مغادرة الضيف — وليس تقييماً للضيف">
                        حالة العقار عند المغادرة
                      </th>
                    )}
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {guest.bookings.map(booking => (
                    <tr key={booking._id}>
                      <td>#{booking._id.slice(-8)}</td>
                      <td>
                        {booking.property?.name || '-'}
                        {booking.property?.propertyCode && (
                          <span className="badge bg-light text-dark ms-2 fw-normal" style={{ fontSize: '0.75em' }} dir="ltr">{booking.property.propertyCode}</span>
                        )}
                      </td>
                      <td>{formatDate(booking.checkIn)}</td>
                      <td>{formatDate(booking.checkOut)}</td>
                      <td>{formatCurrency(booking.totalAmount)}</td>
                      <td>
                        <span className={`status-badge status-${booking.status}`}>
                          {getStatusLabel(booking.status)}
                        </span>
                      </td>
                      {showConditionRating && (
                        <td>
                          {booking.conditionRating ? (
                            <span
                              className="condition-rating"
                              title={booking.conditionRatedAt
                                ? `سُجّل في ${formatDate(booking.conditionRatedAt)}`
                                : undefined}
                            >
                              <i className="bi bi-star-fill"></i>
                              <span dir="ltr">{booking.conditionRating}<span className="of">/5</span></span>
                            </span>
                          ) : (
                            <span className="condition-rating-empty">لم يُقيَّم</span>
                          )}
                        </td>
                      )}
                      <td>
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/bookings/${booking._id}`)}
                        >
                          عرض <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <i className="bi bi-calendar-x"></i>
              <p>لا توجد حجوزات</p>
            </div>
          )}
        </div>
      </div>
    </DesktopLayout>
  );
};

export default GuestDetailsPage;
